import { Divider, Grid, Paper } from "@mui/material";
import React from "react";
import { ReferenceManyField, useRecordContext } from "react-admin";
import { useWebsite } from "../../hooks";
import { imageURL } from "../../http";
import { type Offer } from "../../models";
import { ReviewSimpleList } from "../review/simple";
import { OfferCard } from "./OfferCard";
import { Description } from "./description";
import { ShopGrid } from "./grid";

export const ShopDetails = (): JSX.Element => {
    const offer = useRecordContext<Offer>();
    const variantIndex = React.useState(0);
    const detailIndex = React.useState(0);
    const expandedIndex = React.useState(0);
    const variant = offer ? offer.variants[variantIndex[0]] : undefined;
    const detail = offer && variant ?  variant.details[detailIndex[0]] : undefined;
    const w = useWebsite();
    const brandIds = offer ? offer.variants.flatMap(x => x.details).map(x => x.brandId) : [];
    const brands = w.brands.filter(x => brandIds.includes(x.brandId));

    return offer && variant && detail ? (
        <>
            <meta name="twitter:card" content="summary_large_image" />
            {/* <meta name="twitter:site" content="@nytimes" /> */}
            {/* <meta name="twitter:creator" content="@SarahMaslinNir" /> */}
            <meta name="twitter:title" content={variant.name} />
            <meta name="twitter:description" content={offer.description} />
            {detail.image && <meta name="twitter:image" content={imageURL(detail.image)} />}
            <script
                type="application/ld+json"
                dangerouslySetInnerHTML={{
                    __html: JSON.stringify(ldjson(offer)),
                }}
            />
            <Grid container spacing={1} sx={{ paddingTop: 0.5 }}>
                <Grid size={{ xs: 12, md: 6 }}>
                    <OfferCard
                        sx={{ maxHeight: 512 }}
                        offerIndex={variantIndex}
                        detailIndex={detailIndex}
                        expandedIndex={expandedIndex}
                        offer={{ ...offer, variants: offer.variants.map(variant => {
                            return {
                                ...variant,
                                details: variant.details.map(detail => {
                                    const brand = brands.find(x => x.brandId === detail.brandId)!;
                                    return {
                                        ...detail,
                                        brand: brand?.name,
                                        brandImage: brand?.image,
                                        brandColor: brand?.color,
                                    };
                                }),
                            };
                        })}}
                        currency="USD"
                        taxRate={0}
                        fontSize="1.5rem"
                    />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }} sx={{ display: "flex" }}>
                    <Description description={offer.description} variant={variant} expandedIndex={expandedIndex} detailIndex={detailIndex} />
                </Grid>

                <Grid size={{ xs: 12 }}>
                    <Paper>                        
                        <Divider orientation="horizontal" flexItem>
                            Reviews
                        </Divider>
                        <ReferenceManyField
                            reference="order_detail"
                            record={{ id: detail.brandId }}
                            target="brandId"
                            filter={{ 
                                sellerId: detail.accountId, 
                                productId: detail.productId,
                                "order.status": "Complete",
                                "select@": "*,order!inner(),...account!FK_buyer(name,image)" 
                            }}
                        >
                            <ReviewSimpleList />
                        </ReferenceManyField>
                    </Paper>
                </Grid>
                <Grid size={{ xs: 12 }}>
                    <Paper>  
                    <Divider orientation="horizontal" flexItem>
                            More like this
                    </Divider>
                        <ReferenceManyField
                            reference="collection_offer"
                            record={{ id: detail.accountId }}
                            target="accountId"
                            queryOptions={{ select: ({ data, ...rest }) => ({ data: data
                                .map(({ offer_variant, ...rest }) => ({ ...rest, variants: offer_variant
                                    .map(({ offer_detail, ...rest }: any) => ({ details: offer_detail, ...rest })) })),   ...rest 
                            }) }}
                            filter={{ collectionId: 1, "select@": "*,...offer(*,offer_variant(*,offer_detail(*,...product(*))))" }}
                        >
                            <ShopGrid />
                        </ReferenceManyField>
                        </Paper>
                </Grid>
            </Grid>
        </>
    ) : <></>;
};

function ldjson(offer: Offer) {
    return offer.variants.map(v => {
        const detail = v.details[0];
        const result: any = {
            "@context": "https://schema.org/",
            "@type": "Product",
            "name": offer.name,
            "description": offer.description,
            "gtin": detail.gtin,
            "mpn": detail.mpn,
            "itemCondition": detail.condition,
            "brand": {
                "@type": "Brand",
                "name": detail.brand,
            },
            "category": detail.categoryId,
            "offers": {
                "@type": "Offer",
                "price": v.price,
                "priceCurrency": "USD",
                "availability": "https://schema.org/InStock",
            },
        };

        if (detail.image) {
            result.image = [imageURL(detail.image)];
        } else if (detail.images && detail.images.length > 0) {
            result.image = detail.images.map((img) => imageURL(img));
        }

        if (detail.rating !== undefined) {
            result.aggregateRating = {
                "@type": "AggregateRating",
                "ratingValue": detail.rating,
            };
        }

        return result;
    });
}
